import { useCallback, useContext, useMemo, useState } from "react"
import { SupportedTokenName } from "@mezo/sdk"
import {
  Block,
  ModalBody,
  ParagraphMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Form, FormikProps, withFormik } from "formik"

import {
  RootState,
  selectTokenBalance,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import { FieldAssetInput } from "#/shared/components/forms"
import TermsOfUseCheckbox from "#/shared/components/TermsOfUseCheckbox"
import { FlowContext } from "#/shared/context"
import { useModalCustomOptions } from "#/shared/hooks"
import { useMinBridgeAmount } from "#/shared/hooks/bridge"
import {
  BridgeableTokens,
  bridgeableTokens,
  BridgeAssetFormValue,
} from "#/shared/types"
import { bigIntToFloat, getCurrencySymbol } from "#/shared/utils"

import { StepperAbsoluteModalFooter } from "../StepperModal"
import StepperModalButton from "../StepperModal/StepperModalButton"

import BridgeSummaryList from "./BridgeSummaryList"
import SendFromAnotherWalletField from "./SendFromAnotherWalletField"

type BridgeAssetFormProps = {
  initialValues: BridgeAssetFormValue
  onSubmitForm: (values: BridgeAssetFormValue) => void
}

function BridgeAssetFormBase({
  handleSubmit,
  getFieldHelpers,
  getFieldMeta,
  isValid,
}: FormikProps<BridgeAssetFormValue>) {
  const [css, theme] = useStyletron()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const { isBitcoinFlow } = useContext(FlowContext)

  const { setValue: setShouldUseAnotherWallet } = getFieldHelpers(
    "shouldUseAnotherWallet",
  )

  const { value: shouldUseAnotherWallet } = getFieldMeta<boolean>(
    "shouldUseAnotherWallet",
  )

  const { depositId } = useModalCustomOptions<{ depositId: number }>()

  const { value: amount } = getFieldMeta<bigint | undefined>("amount")
  const { value: token } = getFieldMeta<BridgeableTokens>("token")

  const { data: minTokenAmount, isLoading: isWaitingForMinBridgeAmount } =
    useMinBridgeAmount(token)

  const tokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, token),
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const tooltipContent = useMemo(
    () =>
      token === "BTC" ? "Depositing from BTC wallet" : "Only available for BTC",
    [token],
  )

  const handleSelectToken = useCallback(
    (selectedToken: SupportedTokenName) => {
      if (isBitcoinFlow) return

      setShouldUseAnotherWallet(selectedToken === "BTC")
    },
    [isBitcoinFlow, setShouldUseAnotherWallet],
  )

  return (
    <Form onSubmit={handleSubmit}>
      <ModalBody
        style={{
          marginBottom: theme.sizing.scale4800,
        }}
      >
        <FieldAssetInput
          readOnly={!!depositId}
          withMaxButton
          minTokenAmount={bigIntToFloat(
            minTokenAmount,
            tokenDecimals,
            tokenDecimals,
          )}
          maxTokenAmount={
            shouldUseAnotherWallet
              ? undefined
              : bigIntToFloat(tokenBalance, tokenDecimals, tokenDecimals)
          }
          selectTokenCallback={handleSelectToken}
          tokens={[...bridgeableTokens]}
          checkWalletBalances={false}
          customMessageContent={
            isWaitingForMinBridgeAmount
              ? "Loading min bridge amount..."
              : undefined
          }
        />
        <Block
          as="ul"
          className={css({
            marginTop: theme.sizing.scale800,
            marginLeft: theme.sizing.scale600,
          })}
        >
          <ParagraphMedium as="li">
            In order to borrow, make sure to bridge {getCurrencySymbol("BTC")}{" "}
            or {getCurrencySymbol("tBTC")}. Swaps are coming soon!
          </ParagraphMedium>
          <ParagraphMedium as="li">
            You&apos;ll need to borrow at least 1,800{" "}
            {getCurrencySymbol("MUSD")} to get started.
          </ParagraphMedium>
          <ParagraphMedium as="li">
            Mezo uses {getCurrencySymbol("mBTC")} for gas.
          </ParagraphMedium>
          <ParagraphMedium as="li">
            Bridging funds is currently a one-way operation.
          </ParagraphMedium>
        </Block>
        <SendFromAnotherWalletField
          state={token === "BTC" && isBitcoinFlow ? "enabled" : "disabled"}
          tooltipContent={isBitcoinFlow ? undefined : tooltipContent}
          isChecked={shouldUseAnotherWallet}
          setIsChecked={setShouldUseAnotherWallet}
        />
        <BridgeSummaryList amount={amount} token={token} />
      </ModalBody>
      <StepperAbsoluteModalFooter>
        <TermsOfUseCheckbox
          checked={hasAcceptedTerms}
          onChange={(ev) => setHasAcceptedTerms(ev.target.checked)}
        />
        <StepperModalButton
          type="submit"
          disabled={
            !isValid ||
            amount === undefined ||
            isWaitingForMinBridgeAmount ||
            !hasAcceptedTerms
          }
        >
          Review
        </StepperModalButton>
      </StepperAbsoluteModalFooter>
    </Form>
  )
}

const BridgeAssetForm = withFormik<BridgeAssetFormProps, BridgeAssetFormValue>({
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props }) => props.onSubmitForm(values),
  // Validation is done at the asset input level
  validateOnBlur: false,
  validateOnChange: false,
  validateOnMount: false,
})(BridgeAssetFormBase)

export default BridgeAssetForm
